'use client'

import { useState, useRef, useEffect } from 'react'
import { CheckIcon } from 'lucide-react'
import Link from 'next/link'
import { motion } from 'framer-motion'

export function CreativePricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annually'>('monthly')
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && window.innerWidth < 768) { // Only scroll on mobile (under md breakpoint)
      const proPlanIndex = pricingPlans.findIndex(plan => plan.popular);
      if (proPlanIndex !== -1 && container.children.length > proPlanIndex) {
        const proPlanElement = container.children[proPlanIndex] as HTMLElement;
        const containerWidth = container.offsetWidth;
        const planWidth = proPlanElement.offsetWidth;
        const scrollLeft = proPlanElement.offsetLeft - (containerWidth / 2) + (planWidth / 2);
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, []); // Run only once on mount

  const pricingPlans = [
    {
      name: 'Basic',
      description: 'Media monitoring + sentiment analysis',
      price: null,
      customPriceText: 'TBD',
      features: [
        'Media monitoring across all major platforms',
        'Basic sentiment analysis',
        'Daily reports',
        'Email alerts for new mentions',
        '5 keywords tracking',
        'CSV export'
      ],
      ctaText: 'Get Started',
      ctaLink: '/dashboard',
      popular: false,
    },
    {
      name: 'Pro',
      description: 'Advanced analytics + competitor tracking',
      price: null,
      customPriceText: 'TBD',
      features: [
        'All Basic features',
        'Advanced sentiment analysis',
        'Real-time alerts',
        'Competitor tracking',
        '15 keywords tracking',
        'API access',
        'Custom reports',
        'Dedicated support'
      ],
      ctaText: 'Get Started',
      ctaLink: '/dashboard',
      popular: true,
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions + dedicated support',
      price: null,
      customPriceText: 'Contact Us',
      features: [
        'All Pro features',
        'Unlimited keywords tracking',
        'Custom integrations',
        'Dedicated account manager',
        'Priority support',
        'Custom AI models',
        'White-label reports',
        'Advanced API access'
      ],
      ctaText: 'Contact Sales',
      ctaLink: '/dashboard',
      popular: false,
    },
  ]

  return (
    <div className="relative mx-auto w-full max-w-7xl px-4 py-8">
      {/* Toggle */}
      <div className="mx-auto mb-8 flex w-fit items-center gap-4 rounded-full bg-gray-800 p-1">
        <button
          onClick={() => setBillingCycle('monthly')}
          className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${
            billingCycle === 'monthly' 
              ? 'bg-[#100D1C] text-white shadow-sm'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle('annually')}
          className={`rounded-full px-6 py-2 text-sm font-medium transition-all ${
            billingCycle === 'annually' 
              ? 'bg-[#100D1C] text-white shadow-sm'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Annually
          <span className="ml-1 rounded-full bg-[#8A6FE8] px-2 py-0.5 text-xs text-white">
            Save 20%
          </span>
        </button>
      </div>

      {/* Plans */}
      <div ref={scrollContainerRef} className="flex overflow-x-auto snap-x snap-mandatory space-x-4 pb-4 md:grid md:grid-cols-3 md:space-x-0 md:gap-6 md:pb-0 no-scrollbar">
        {pricingPlans.map((plan, i) => (
          <motion.div
            key={plan.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className={`relative flex flex-col overflow-hidden rounded-2xl bg-[#100D1C] border transition-all w-72 flex-shrink-0 snap-center md:w-auto ${
              plan.popular
                ? 'border-[#8A6FE8] ring-2 ring-[#8A6FE8]/30'
                : 'border-gray-800 hover:border-[#8A6FE8]/50 hover:shadow-md hover:shadow-[#8A6FE8]/10'
            }`}
          >
            {plan.popular && (
              <div className="absolute right-0 top-0 z-10 rounded-bl-xl bg-[#8A6FE8] px-3 py-1 text-xs font-medium text-white">
                Most Popular
              </div>
            )}
            <div className="space-y-4 p-5">
              <div>
                <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                <p className="mt-1 text-sm text-gray-400">{plan.description}</p>
              </div>
              <div className="space-y-1">
                {plan.price ? (
                  <>
                    <p className="text-3xl font-bold text-white">
                      ${plan.price}
                      <span className="text-base font-normal text-gray-400">
                        /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                      </span>
                    </p>
                    <p className="text-sm text-gray-400">
                      {billingCycle === 'monthly' ? 'Billed monthly.' : 'Billed annually.'}
                    </p>
                  </>
                ) : (
                  <p className="text-3xl font-bold text-white">{plan.customPriceText}</p>
                )}
              </div>
            </div>
            <div className="flex h-full flex-col justify-between">
              <div className="space-y-4 p-5 pt-0">
                <ul className="space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-gray-300">
                      <CheckIcon className="h-5 w-5 shrink-0 text-[#8A6FE8]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-5 pt-0">
                <Link
                  href={plan.ctaLink}
                  className={`block rounded-lg px-4 py-2 text-center text-sm font-medium transition-colors ${
                    plan.popular
                      ? 'bg-[#8A6FE8] text-white hover:bg-[#7650E0]'
                      : 'bg-gray-800 text-white hover:bg-gray-700'
                  }`}
                >
                  {plan.ctaText}
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}